import type { WireBundleSimulation, WireTotals } from "../../../lib/api";
import { formatUsdcRaw } from "../../../lib/format";

interface Props {
	totals: WireTotals;
	bundles?: WireBundleSimulation[];
}

/**
 * `CostBreakdown` — prominent "what will this bundle actually cost"
 * panel that sits in the cart's simulation block.
 *
 * Surfaces three things side-by-side and trustworthy:
 *
 *   - **Total** in USDC, big — the headline number the user cares about
 *     before clicking Execute.
 *   - **Gas** — both USDC equivalent (via the backend's native-price
 *     oracle) AND raw ETH so the user can sanity-check against any
 *     mempool/gas-tracker. Falls back to "no bundle cost rows" when
 *     gas estimation hasn't run (e.g. plan-stage failure).
 *   - **Swap slippage + protocol fees** — split lines, hidden when
 *     each is zero (avoids "swap $0.00" noise on a no-swap rebalance).
 *
 * The line under "Gas" shows `weighted gwei × total units = ETH` so
 * the relationship is obvious — on dev runs against anvil the
 * weighted price will read as ~1 gwei (anvil's default fake base fee)
 * which is a useful tell that you're on the fork.
 *
 * The component is intentionally pure presentation — every number
 * comes from the wire `totals` + `bundles`. We compute three derived
 * numbers locally:
 *   - protocolUsdcRaw = total − gas − swap (backend doesn't aggregate
 *     this today; the per-bundle slot is a placeholder for the future
 *     protocol-fee registry, so we infer it from the residual);
 *   - totalWeiUsed   = Σ over bundles of gasUnits × gasPriceWei;
 *   - weightedGwei   = totalWeiUsed / totalGasUnits, in gwei.
 */
export function CostBreakdown({ totals, bundles }: Props) {
	const gas = BigInt(totals.gasCostUsdcRaw);
	const swap = BigInt(totals.swapCostUsdcRaw);
	const total = BigInt(totals.totalCostUsdcRaw);
	// `protocol` is the residual the backend rolled into `total` beyond
	// gas + swap. Today's `EstimateBundleCost` always sets it to 0 (the
	// per-protocol fee registry isn't shipped yet) so this stays hidden
	// in practice — but inferring rather than reading a missing field
	// keeps the breakdown forward-compatible when fees do start flowing.
	const protocol = total - gas - swap;

	const { ethText, gweiText, gasUnitsText } = summariseGas(bundles, totals);

	return (
		<div className="space-y-2 rounded-md border border-slate-700/60 bg-slate-900/40 p-3 text-[11px]">
			<div className="flex items-baseline justify-between gap-2">
				<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
					Estimated TX cost
				</span>
				<span className="font-mono text-base text-slate-100">
					{formatUsdcRaw(total.toString())}
				</span>
			</div>
			<dl className="space-y-1 font-mono text-slate-300">
				<Row label="Gas" value={formatUsdcRaw(gas.toString())}>
					<span className="text-slate-500">
						{gasUnitsText}
						{gweiText !== null && <> @ {gweiText} gwei</>}
						{ethText !== null && <> ≈ {ethText} ETH</>}
					</span>
				</Row>
				{swap > 0n && (
					<Row label="Swap slippage" value={formatUsdcRaw(swap.toString())} />
				)}
				{protocol > 0n && (
					<Row
						label="Protocol fees"
						value={formatUsdcRaw(protocol.toString())}
					/>
				)}
			</dl>
		</div>
	);
}

function Row({
	label,
	value,
	children,
}: {
	label: string;
	value: string;
	children?: React.ReactNode;
}) {
	return (
		<div className="flex items-baseline justify-between gap-3">
			<div className="flex flex-col">
				<span className="text-slate-400">{label}</span>
				{children && <span className="text-[10px]">{children}</span>}
			</div>
			<span className="text-slate-200">{value}</span>
		</div>
	);
}

/**
 * Cross-bundle gas summary. Sums `gasUnits × gasPriceWei` (in wei,
 * stays bigint) so deep mainnet gas doesn't overflow `Number`; the
 * final ETH + gwei strings are formatted from the bigint via
 * fixed-decimal helpers below.
 *
 * Returns null fields when bundle costs are unavailable (e.g. a
 * plan-stage failure where `EstimateBundleCost` never ran) — caller
 * still renders the gas USDC subtotal from `totals.gasCostUsdcRaw`
 * but skips the "@ X gwei ≈ Y ETH" annotation.
 */
function summariseGas(
	bundles: WireBundleSimulation[] | undefined,
	totals: WireTotals,
): { ethText: string | null; gweiText: string | null; gasUnitsText: string } {
	const totalUnits = BigInt(totals.gasUnits);
	const gasUnitsText = `${Number(totalUnits).toLocaleString("en-US")} units`;
	if (!bundles || bundles.length === 0) {
		return { ethText: null, gweiText: null, gasUnitsText };
	}
	let totalWei = 0n;
	let unitsSeen = 0n;
	for (const b of bundles) {
		if (!b.cost) continue;
		totalWei += BigInt(b.cost.gasUnits) * BigInt(b.cost.gasPriceWei);
		unitsSeen += BigInt(b.cost.gasUnits);
	}
	if (totalWei === 0n) {
		return { ethText: null, gweiText: null, gasUnitsText };
	}
	return {
		ethText: weiToEthDisplay(totalWei),
		gweiText: unitsSeen > 0n ? weightedGweiDisplay(totalWei, unitsSeen) : null,
		gasUnitsText,
	};
}

/**
 * `wei` → "0.001670" (6 fractional digits — enough to show
 * 0.0000XX ETH for low-gas L2-style txs, capped to keep the line
 * tight). Pure bigint maths until the final `slice` so wei values
 * above 2^53 don't lose precision.
 */
function weiToEthDisplay(wei: bigint): string {
	const ONE_ETH = 10n ** 18n;
	const whole = wei / ONE_ETH;
	const frac = (wei % ONE_ETH).toString().padStart(18, "0").slice(0, 6);
	return `${whole}.${frac}`;
}

/**
 * Weighted gwei = (totalWei / totalUnits) / 1e9. We do the division
 * in bigint to get weiPerUnit, then cast to Number once it's safely
 * below 2^53 (a single gas-unit cost in wei is always small).
 */
function weightedGweiDisplay(totalWei: bigint, totalUnits: bigint): string {
	const weiPerUnit = totalWei / totalUnits; // truncates — fine for display
	const gwei = Number(weiPerUnit) / 1e9;
	if (gwei >= 100) return gwei.toFixed(0);
	if (gwei >= 10) return gwei.toFixed(1);
	return gwei.toFixed(2);
}
